import { GeneralAssignmentConsolidatedService } from './generalAssignmentConsolidatedService';
import { RoutineAssignmentConsolidatedService } from './routineAssignmentConsolidatedService';
import { AssignmentStatusValue } from '../types/alarm';
import { OptimizedAssignment } from '../types';
import { assignmentMemoService } from './assignmentMemoService';


/**
 * 최적화된 과제 서비스 V3
 * studentStatuses를 generalAssignmentStatuses 하위 컬렉션으로 분리
 */
export const optimizedAssignmentServiceV3 = {
  /**
   * 일반 과제 로드 (새로운 통합 구조)
   */
  async loadAssignments(userId: string, classId: string): Promise<OptimizedAssignment[]> {
    // 새로운 통합 구조 사용
    const consolidatedService = new GeneralAssignmentConsolidatedService(userId, classId);
    
    try {
      const assignmentInfos = await consolidatedService.loadAllGeneralAssignments();
      
      // GeneralAssignmentInfo를 OptimizedAssignment로 변환
      const assignments: OptimizedAssignment[] = assignmentInfos.map(info => ({
        id: info.assignmentId,
        title: info.title,
        description: info.description,
        dueDate: new Date(info.dueDate),
        basePoints: info.basePoints,
        assignedStudentIds: info.assignedStudentIds,
        createdAt: info.createdAt,
        updatedAt: info.updatedAt || info.lastUpdated,
        isActive: info.isActive,
        categoryId: info.categoryId,
        icon: info.icon || '',
        notes: info.notes || '',
        studentStatuses: [], // OptimizedAssignment에서는 빈 배열 (별도 관리)
        classId: classId // 클래스 ID 추가
      }));
      
      console.log(`✅ 새로운 구조: 일반 과제 로드 완료 - ${assignments.length}개`);
      return assignments;
    } catch (error) {
      console.error('❌ 새로운 구조: 일반 과제 로드 실패:', error);
      throw error;
    }
  },

  /**
   * 일반 과제의 여러 멤버 상태 일괄 업데이트 (새로운 통합 구조)
   */
  async batchUpdateAssignmentStudentStatuses(
    userId: string, 
    classId: string, 
    assignmentId: string, 
    updates: Array<{
      studentId: string;
      status: AssignmentStatusValue;
      earnedPoints: number;
      memo?: string;
      submissionLink?: string;
      submissionMessage?: string;
      submittedAt?: Date;
      submissionImagePath?: string;
      submissionImageUpdatedAt?: string;
      submissionImageSizeBytes?: number;
    }>
  ): Promise<void> {
    try {
      // 새로운 통합 구조 사용 - 배치 처리로 최적화
      const consolidatedService = new GeneralAssignmentConsolidatedService(userId, classId);
      
      await assignmentMemoService.batchSaveMemos(
        userId,
        classId,
        updates.map(update => ({
          assignmentKey: assignmentId,
          studentId: update.studentId,
          memo: update.memo || ''
        }))
      );

      // 배치로 한 번에 업데이트 (성능 최적화) - memo는 assignmentMemo 컬렉션에서 관리
      await consolidatedService.batchUpdateStudentStatuses(
        assignmentId,
        updates.map(update => {
          const mappedUpdate: any = {
            studentId: update.studentId,
            status: update.status,
            points: update.earnedPoints
          };
          
          // 제출 확인 데이터 포함 (있는 경우)
          if (update.hasOwnProperty('submissionLink')) {
            mappedUpdate.submissionLink = update.submissionLink;
          }
          if (update.hasOwnProperty('submissionMessage')) {
            mappedUpdate.submissionMessage = update.submissionMessage;
          }
          if (update.hasOwnProperty('submittedAt')) {
            mappedUpdate.submittedAt = update.submittedAt;
          }
          if (update.hasOwnProperty('submissionImagePath')) {
            mappedUpdate.submissionImagePath = update.submissionImagePath;
          }
          if (update.hasOwnProperty('submissionImageUpdatedAt')) {
            mappedUpdate.submissionImageUpdatedAt = update.submissionImageUpdatedAt;
          }
          if (update.hasOwnProperty('submissionImageSizeBytes')) {
            mappedUpdate.submissionImageSizeBytes = update.submissionImageSizeBytes;
          }
          
          return mappedUpdate;
        })
      );
      
      console.log(`✅ 새로운 구조: 일반 과제 멤버 상태 배치 업데이트 완료: ${assignmentId} - ${updates.length}명`);                                                                                               
    } catch (error) {
      console.error('❌ 새로운 구조: 일반 과제 멤버 상태 배치 업데이트 실패:', error);
      throw error;
    }
  },

  /**
   * 일반 과제 멤버 상태 업데이트 (개별)
   */
  async updateAssignmentStudentStatus(
    userId: string, 
    classId: string, 
    assignmentId: string, 
    studentId: string,
    status: AssignmentStatusValue, 
    earnedPoints: number = 0, 
    memo: string = ''
  ): Promise<void> {
    try {
      const consolidatedService = new GeneralAssignmentConsolidatedService(userId, classId);
      const assignmentInfo = await consolidatedService.loadGeneralAssignment(assignmentId);
      if (!assignmentInfo) {
        throw new Error('과제를 찾을 수 없습니다.');
      }
      const studentStatuses = assignmentInfo.studentStatuses || [];
      const existingStatusIndex = studentStatuses.findIndex((s: any) => s.studentId === studentId);
      void studentStatuses;
      void existingStatusIndex;

      await assignmentMemoService.saveMemo(
        userId,
        classId,
        assignmentId,
        studentId,
        memo
      );
      await consolidatedService.updateStudentStatus(
        assignmentId,
        studentId,
        status,
        earnedPoints,
        ''
      );
      
      
      console.log(`✅ 새로운 구조: 일반 과제 멤버 상태 업데이트 완료: ${studentId} - ${status}`);
    } catch (error) {
      console.error('과제 멤버 상태 업데이트 실패:', error);
      throw error;
    }
  },

  /**
   * 프로젝트 하위 과제의 멤버 상태 업데이트
   */
  async updateProjectSubTaskStudentStatus(
    userId: string,
    classId: string,
    projectId: string,
    subTaskId: string,
    studentId: string,
    status: AssignmentStatusValue,
    earnedPoints: number = 0,
    memo: string = '',
    memberGroup?: string
  ): Promise<void> {
    try {
      console.log('🚀 updateProjectSubTaskStudentStatus 호출됨 (통합 문서 구조):', {
        userId,
        classId,
        projectId,
        subTaskId,
        studentId,
        status,
        earnedPoints,
        memo
      });

      // 통합 문서 서비스 사용
      const { ProjectAssignmentConsolidatedService } = await import('./projectAssignmentConsolidatedService');
      const consolidatedService = new ProjectAssignmentConsolidatedService(userId, classId, memberGroup);

      await assignmentMemoService.saveMemo(
        userId,
        classId,
        `${projectId}-${subTaskId}`,
        studentId,
        memo,
        undefined,
        undefined,
        memberGroup
      );
      await consolidatedService.updateSubTaskStudentStatus(
        projectId,
        subTaskId,
        studentId,
        status,
        earnedPoints,
        ''
      );

      console.log(`✅ 프로젝트 과제 멤버 상태 업데이트 완료: ${projectId}-${subTaskId} - ${studentId} - ${status}`);
    } catch (error) {
      console.error('프로젝트 과제 멤버 상태 업데이트 실패:', error);
      throw error;
    }
  },

  /**
   * 프로젝트 과제 하위 과제의 여러 멤버 상태 일괄 업데이트 (새로운 통합 구조)
   */
  async batchUpdateProjectSubTaskStudentStatuses(
    userId: string, 
    classId: string, 
    projectId: string, 
    subTaskId: string,
    updates: Array<{
      studentId: string;
      status: AssignmentStatusValue;
      earnedPoints: number;
      memo?: string;
      submissionLink?: string;
      submissionMessage?: string;
      submittedAt?: Date;
      submissionImagePath?: string;
      submissionImageUpdatedAt?: string;
      submissionImageSizeBytes?: number;
    }>,
    memberGroup?: string
  ): Promise<void> {
    try {
      // 통합 문서 서비스 사용 - 배치 처리로 최적화
      const { ProjectAssignmentConsolidatedService } = await import('./projectAssignmentConsolidatedService');
      const consolidatedService = new ProjectAssignmentConsolidatedService(userId, classId, memberGroup);

      await assignmentMemoService.batchSaveMemos(
        userId,
        classId,
        updates.map(update => ({
          assignmentKey: `${projectId}-${subTaskId}`,
          studentId: update.studentId,
          memo: update.memo || ''
        })),
        memberGroup
      );
      
      // 배치로 한 번에 업데이트 (성능 최적화) - memo는 assignmentMemo 컬렉션에서 관리
      await consolidatedService.batchUpdateSubTaskStudentStatuses(
        projectId,
        subTaskId,
        updates.map(update => {
          const mappedUpdate: any = {
            studentId: update.studentId,
            status: update.status,
            points: update.earnedPoints
          };
          
          // 제출 확인 데이터 포함 (있는 경우)
          if (update.hasOwnProperty('submissionLink')) {
            mappedUpdate.submissionLink = update.submissionLink;
          }
          if (update.hasOwnProperty('submissionMessage')) {
            mappedUpdate.submissionMessage = update.submissionMessage;
          }
          if (update.hasOwnProperty('submittedAt')) {
            mappedUpdate.submittedAt = update.submittedAt;
          }
          if (update.hasOwnProperty('submissionImagePath')) {
            mappedUpdate.submissionImagePath = update.submissionImagePath;
          }
          if (update.hasOwnProperty('submissionImageUpdatedAt')) {
            mappedUpdate.submissionImageUpdatedAt = update.submissionImageUpdatedAt;
          }
          if (update.hasOwnProperty('submissionImageSizeBytes')) {
            mappedUpdate.submissionImageSizeBytes = update.submissionImageSizeBytes;
          }
          
          return mappedUpdate;
        })
      );
      
      console.log(`✅ 새로운 구조: 프로젝트 과제 하위 과제 멤버 상태 배치 업데이트 완료: ${projectId}-${subTaskId} - ${updates.length}명`);                                                                                               
    } catch (error) {
      console.error('❌ 새로운 구조: 프로젝트 과제 하위 과제 멤버 상태 배치 업데이트 실패:', error);
      throw error;
    }
  },

  /**
   * 루틴 과제 날짜별 멤버 상태 업데이트 (통합 문서 구조 사용)
   */
  async updateRoutineAssignmentDateStudentStatus(
    userId: string,
    classId: string,
    routineId: string,
    dateId: string,
    studentId: string,
    status: AssignmentStatusValue,
    earnedPoints: number = 0,
    memo: string = '',
    memberGroup?: string
  ): Promise<void> {
    try {
      console.log('🚀 updateRoutineAssignmentDateStudentStatus 호출됨 (통합 문서 구조):', {
        userId,
        classId,
        routineId,
        dateId,
        studentId,
        status,
        earnedPoints,
        memo
      });
      
      // 통합 문서 서비스 사용
      const consolidatedService = new RoutineAssignmentConsolidatedService(userId, classId, memberGroup);

      await assignmentMemoService.saveMemo(
        userId,
        classId,
        `${routineId}-${dateId}`,
        studentId,
        memo,
        undefined,
        undefined,
        memberGroup
      );
      await consolidatedService.updateStudentStatus(
        routineId,
        dateId,
        studentId,
        status,
        earnedPoints,
        ''
      );
      
      console.log(`✅ 루틴 과제 멤버 상태 업데이트 완료: ${routineId}-${dateId} - ${studentId} - ${status}`);
    } catch (error) {
      console.error('루틴 과제 멤버 상태 업데이트 실패:', error);
      throw error;
    }
  },

  /**
   * 루틴 과제: 특정 날짜에 대해 멤버 상태·메모를 일괄 반영 (통합 문서 읽기 최소화)
   */
  async batchUpdateRoutineAssignmentDateStudentStatuses(
    userId: string,
    classId: string,
    routineId: string,
    dateId: string,
    studentUpdates: Array<{
      studentId: string;
      status: AssignmentStatusValue;
      earnedPoints: number;
      memo?: string;
    }>,
    memberGroup?: string,
    options?: { fillMissingAssignedAsPending?: boolean; assignedStudentIds?: string[] }
  ): Promise<void> {
    if (!studentUpdates.length && !options?.fillMissingAssignedAsPending) {
      return;
    }

    const assignmentKey = `${routineId}-${dateId}`;
    const memoPayload = studentUpdates.map(u => ({
      assignmentKey,
      studentId: u.studentId,
      memo: u.memo ?? ''
    }));

    try {
      const consolidatedService = new RoutineAssignmentConsolidatedService(userId, classId, memberGroup);

      if (memoPayload.length > 0) {
        await assignmentMemoService.batchSaveMemos(userId, classId, memoPayload, memberGroup);
      }

      const statusPayload = studentUpdates.map(u => ({
        studentId: u.studentId,
        status: u.status,
        points: u.earnedPoints ?? 0
      }));

      const { studentIdsForRewardPass, rewardsAlreadyHandled } =
        await consolidatedService.batchUpdateStudentStatusesForDate(
          routineId,
          dateId,
          statusPayload,
          options
        );

      if (!rewardsAlreadyHandled && studentIdsForRewardPass.length > 0) {
        await consolidatedService.runRoutineRewardsForStudentsSequential(
          routineId,
          studentIdsForRewardPass
        );
      }

      console.log(
        `✅ 루틴 과제 멤버 상태 배치 업데이트 완료: ${routineId}-${dateId} — ${studentUpdates.length}건`
      );
    } catch (error) {
      console.error('루틴 과제 멤버 상태 배치 업데이트 실패:', error);
      throw error;
    }
  }
};